import type {
  CreateEstimateRequest,
  GrowerListResponse,
  HarvestWeekImportRequest,
  HarvestWeekImportResult,
  HarvestWeekListResponse,
  Estimate,
  EstimateListResponse,
  LoginRequest,
  LoginResponse,
  MasterData,
  UpdateEstimateRequest,
  User,
  UserFarmImportRequest,
  UserFarmImportResult,
  UserFarmListResponse,
} from '@fruitways/shared';
import { request, setAccessToken } from './client';

export const api = {
  auth: {
    async login(body: LoginRequest): Promise<LoginResponse> {
      const res = await request<LoginResponse>('/api/auth/login', {
        method: 'POST',
        body,
        anonymous: true,
      });
      setAccessToken(res.accessToken);
      return res;
    },
    async logout(): Promise<void> {
      try {
        await request<void>('/api/auth/logout', { method: 'POST', anonymous: true });
      } finally {
        setAccessToken(null);
      }
    },
    me: () => request<User>('/api/auth/me'),
    forgotPassword: (username: string) =>
      request<void>('/api/auth/forgot-password', {
        method: 'POST',
        body: { username },
        anonymous: true,
      }),
    resetPassword: (token: string, password: string) =>
      request<void>('/api/auth/reset-password', {
        method: 'POST',
        body: { token, password },
        anonymous: true,
      }),
  },
  masterData: () => request<MasterData>('/api/master-data'),
  estimates: {
    list: (limit = 4) =>
      request<EstimateListResponse>(`/api/estimates?limit=${limit}`).then((r) => r.estimates),
    get: (id: string) => request<Estimate>(`/api/estimates/${id}`),
    create: (body: CreateEstimateRequest) =>
      request<Estimate>('/api/estimates', { method: 'POST', body }),
    update: (id: string, body: UpdateEstimateRequest) =>
      request<Estimate>(`/api/estimates/${id}`, { method: 'PUT', body }),
    remove: (id: string) => request<void>(`/api/estimates/${id}`, { method: 'DELETE' }),
  },
  /** Admin only. Master data maintenance; always needs signal, never queued. */
  admin: {
    growers: () => request<GrowerListResponse>('/api/admin/growers').then((r) => r.growers),
    harvestWeeks: {
      list: () =>
        request<HarvestWeekListResponse>('/api/admin/harvest-weeks').then((r) => r.harvestWeeks),
      /** Check only, or with `commit` replace every variety harvest week with the file's rows. */
      import: (body: HarvestWeekImportRequest) =>
        request<HarvestWeekImportResult>('/api/admin/harvest-weeks/import', {
          method: 'POST',
          body,
        }),
    },
    userFarms: {
      list: () => request<UserFarmListResponse>('/api/admin/user-farms').then((r) => r.userFarms),
      /** Check only, or with `commit` apply the file: add, update, and deactivate what it omits. */
      import: (body: UserFarmImportRequest) =>
        request<UserFarmImportResult>('/api/admin/user-farms/import', { method: 'POST', body }),
    },
  },
  health: () => request<{ ok: true }>('/api/health', { anonymous: true }),
};
